import React, { useContext, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Grid2 as Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import {
    ArrowForwardRounded,
    ChatBubbleOutlineRounded,
    ContactSupportRounded,
    SendRounded,
    SubjectRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { Support_add } from "../../network/api/support.api";
import _SupportInfo from "./_SupportInfo";

const NewSupport = () => {
    const theme = useTheme();
    const error = useContext(ErrorContext);
    const navigate = useNavigate();
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedTitle = title.trim();
        const trimmedMessage = message.trim();

        if (!trimmedTitle) {
            error.showError({ message: "لطفاً موضوع تیکت را وارد نمایید." });
            return;
        }
        if (trimmedTitle.length > 50) {
            error.showError({ message: "موضوع تیکت نمی‌تواند بیشتر از ۵۰ کاراکتر باشد." });
            return;
        }
        if (!trimmedMessage) {
            error.showError({ message: "لطفاً متن درخواست تیکت را وارد نمایید." });
            return;
        }
        if (trimmedMessage.length > 500) {
            error.showError({ message: "متن تیکت نمی‌تواند بیشتر از ۵۰۰ کاراکتر باشد." });
            return;
        }

        setSubmitting(true);
        try {
            const result = await Support_add({
                Title: trimmedTitle,
                Message: {
                    Status: "AWAITING_EXPERT",
                    Message: trimmedMessage,
                    IsRead: true,
                },
                CorporateId: corporate?.Id,
            });

            const newTicketId = result?.data?.Data?.Id;
            if (newTicketId) {
                navigate(`/support/detail/${newTicketId}`);
            } else {
                navigate('/support');
            }
        } catch (err) {
            const errMsg = err?.response?.data?.Message || "خطا در ثبت تیکت پشتیبانی. لطفاً مجدداً تلاش کنید.";
            error.showError({ message: errMsg });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <title>ثبت تیکت جدید | جیم‌پین سازمانی</title>

            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3.5,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 8px 20px rgba(45, 107, 226, 0.3)',
                        }}
                    >
                        <ContactSupportRounded sx={{ fontSize: 30 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            ثبت درخواست پشتیبانی جدید
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            موضوع و شرح مشکل خود را با دقت ثبت کنید تا کارشناسان ما بررسی کنند
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<ArrowForwardRounded />}
                    onClick={() => navigate('/support')}
                    sx={{ borderRadius: 3, fontWeight: 700 }}
                >
                    بازگشت به لیست تیکت‌ها
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Form Column */}
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                            <Box component="form" onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        label="موضوع تیکت"
                                        placeholder="مثلاً: مغایرت در شارژ اعتبار پرسنل، درخواست فاکتور ..."
                                        variant="outlined"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        disabled={submitting}
                                        inputProps={{ maxLength: 50 }}
                                        helperText={`${title.length} / ۵۰ کاراکتر`}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SubjectRounded color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 3,
                                            },
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={6}
                                        maxRows={10}
                                        label="شرح درخواست"
                                        placeholder="جزئیات درخواست یا مشکل خود را به همراه کد ملی پرسنل یا شناسه فاکتور مربوطه بنویسید..."
                                        variant="outlined"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        disabled={submitting}
                                        inputProps={{ maxLength: 500 }}
                                        helperText={`${message.length} / ۵۰۰ کاراکتر`}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                                    <ChatBubbleOutlineRounded color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 3,
                                            },
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2.5,
                                            bgcolor: 'action.hover',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            💡 <strong>نکته:</strong> همکاران ما در سریع‌ترین زمان ممکن پاسخ تیکت شما را ثبت خواهند کرد و پیامک وضعیت برای مدیر سازمان ارسال می‌شود.
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate('/support')}
                                            disabled={submitting}
                                            sx={{ borderRadius: 3, px: 3, py: 1 }}
                                        >
                                            انصراف
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={submitting || !title.trim() || !message.trim()}
                                            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendRounded sx={{ transform: 'rotate(180deg)' }} />}
                                            sx={{
                                                borderRadius: 3,
                                                px: 4,
                                                py: 1.2,
                                                fontWeight: 700,
                                                boxShadow: '0 8px 16px rgba(45, 107, 226, 0.25)',
                                            }}
                                        >
                                            {submitting ? "در حال ثبت تیکت..." : "ارسال و ثبت تیکت"}
                                        </Button>
                                    </Box>
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Info Column */}
                <Grid size={{ xs: 12, lg: 4 }}>
                    <_SupportInfo />
                </Grid>
            </Grid>
        </Container>
    );
};

export default NewSupport;
